import React, { useMemo, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { Stock } from '../StockComparison';
import { CustomTooltip } from './CustomTooltip';

interface TableViewProps {
  readonly stocks: ReadonlyArray<Stock>;
  readonly tableMetrics: ReadonlyArray<any>;
}

export function TableView({
  stocks,
  tableMetrics
}: TableViewProps) {
  if (stocks.length === 0) {
    return <div className="text-theme-secondary text-center py-8">
      No stocks selected to compare
    </div>;
  }

  // Heatmap and grouping state
  const [heatmapOn, setHeatmapOn] = useState(false);
  const [heatScale, setHeatScale] = useState<number>(50); // fixed sensitivity; UI removed

  // Determine if higher or lower is better for a metric id using robust pattern matching
  const getPolarity = (metricId: string): number => {
    const id = String(metricId || '').toLowerCase();
    // Lower is better: valuation multiples (P/E, P/S)
    const isPE = /(^|_|\b)(ttm)?(2y|two)?(year)?forward?pe(\b|_|$)|(^|\b)p\/e(\b|$)/.test(id) || /(^|\b)pe(\b|$)/.test(id) && !/eps/.test(id);
    const isPS = /(ttm|ntm)?ps[_]?ratio|psratio|(^|\b)p\/s(\b|$)|(^|\b)forward[_]?ps(\b|$)/.test(id);
    if (isPE || isPS) return -1;
    // Higher is better: growth and margins
    const isGrowth = /growth/.test(id);
    const isMargins = /margin/.test(id);
    const explicitHigher = ['ttmepsgrowth','forward_12m_eps_growth','ttmrevenuegrowth','nextyearrevenuegrowth','grossmargin','netmargin','operatingmargin'];
    if (isGrowth || isMargins || explicitHigher.includes(id)) return 1;
    return 0;
  };

  // Compute relative score within a row across stocks (0..1), adjusted by polarity
  const getCellStyle = (metricId: string, rowValues: (number | null)[], idx: number) => {
    if (!heatmapOn) return { className: 'text-theme-primary', style: {} as React.CSSProperties };
    const values = rowValues.filter((v): v is number => typeof v === 'number' && isFinite(v));
    if (values.length <= 1) return { className: 'text-theme-primary', style: {} as React.CSSProperties };
    const min = Math.min(...values);
    const max = Math.max(...values);
    const val = rowValues[idx];
    if (typeof val !== 'number' || !isFinite(val) || max === min) return { className: 'text-theme-primary', style: {} as React.CSSProperties };
    let score = (val - min) / (max - min); // 0..1 higher worse/better depending on polarity
    const polarity = getPolarity(metricId);
    if (polarity < 0) score = 1 - score; // lower is better -> invert
    if (polarity === 0) score = 0.5; // neutral
    const intensity = 0.12 + Math.min(1, Math.abs(score - 0.5) * 2) * (heatScale / 100) * 0.18; // 0.12..0.3
    const greenish = score >= 0.5;
    const bg = greenish ? `rgba(16,185,129, ${intensity})` : `rgba(239,68,68, ${intensity})`;
    const color = greenish ? 'text-green-300' : 'text-red-300';
    return { className: color, style: { backgroundColor: bg } as React.CSSProperties };
  };

  // Info popover (click to open, portal-rendered to avoid clipping)
  const [showInfo, setShowInfo] = useState(false);
  const [infoPos, setInfoPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const infoBtnRef = useRef<HTMLDivElement | null>(null);
  const closeInfo = () => setShowInfo(false);
  const openInfo = () => {
    const rect = infoBtnRef.current?.getBoundingClientRect();
    if (rect) {
      const margin = 8;
      const width = 320; // popover width
      let left = Math.min(Math.max(rect.left - (width - rect.width) / 2, 8), (window.innerWidth - width - 8));
      const top = rect.bottom + margin;
      setInfoPos({ top, left });
    }
    setShowInfo(true);
  };
  useEffect(() => {
    if (!showInfo) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeInfo(); };
    const onClick = (e: MouseEvent) => {
      // close when clicking outside
      const el = e.target as Node;
      const pop = document.getElementById('heatmap-popover');
      if (pop && !pop.contains(el) && !infoBtnRef.current?.contains(el as Node)) closeInfo();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [showInfo]);

  return (
    <div className="overflow-x-auto scrollbar-hide relative">
      <div className="sticky top-0 z-30 -mt-px mb-2 px-1 py-1 bg-theme-secondary/95 backdrop-blur supports-[backdrop-filter]:backdrop-blur flex items-center justify-between">
        <div className="text-xs text-theme-muted flex items-center gap-1">
          Heatmap
          <div ref={infoBtnRef} className="inline-flex items-center" aria-label="Heatmap info">
            <button type="button" onClick={() => (showInfo ? closeInfo() : openInfo())} className="p-0.5 rounded hover:bg-theme-tertiary/60" aria-expanded={showInfo} aria-controls="heatmap-popover">
              <Info className="w-4 h-4 text-theme-muted hover:text-theme-primary" />
            </button>
          </div>
          {showInfo && createPortal(
            <div id="heatmap-popover" role="dialog" aria-modal="true" className="fixed z-[9999]" style={{ top: infoPos.top, left: infoPos.left, width: 320 }}>
              <div className="bg-theme-primary border border-theme-tertiary rounded-md p-3 shadow-2xl">
                <p className="text-theme-primary font-medium">What the colors mean</p>
                <ul className="list-disc ml-4 mt-2 space-y-1 text-theme-secondary text-[12px] leading-snug">
                  <li>We compare companies inside each metric row only.</li>
                  <li><span className="text-green-300 font-medium">Greener</span> cells are doing better for that metric; <span className="text-red-300 font-medium">redder</span> cells are doing worse.</li>
                  <li>For price ratios like <b>P/E</b> and <b>P/S</b>, lower is better.</li>
                  <li>For growth and profit numbers like <b>Revenue Growth</b>, <b>EPS Growth</b>, and <b>Margins</b>, higher is better.</li>
                  <li>Colors are <b>relative</b> to the selected companies — there are no fixed cutoffs or thresholds.</li>
                  <li>“N/A” values aren’t colored.</li>
                </ul>
                <div className="mt-2 text-right">
                  <button onClick={closeInfo} className="text-xs px-2 py-1 rounded bg-theme-tertiary text-theme-primary border border-theme-tertiary hover:bg-theme-tertiary/70">Close</button>
                </div>
              </div>
            </div>, document.body)
          }
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setHeatmapOn(v => !v)}
            className={`inline-flex items-center rounded-full border border-theme-tertiary transition-colors ${heatmapOn ? 'bg-green-600/30 text-green-300' : 'bg-theme-tertiary/50 text-theme-secondary'} px-2 py-0.5 text-[11px]`}
            title={heatmapOn ? 'Heatmap: On' : 'Heatmap: Off'}
          >
            HM
            <span className="ml-1 inline-block h-3 w-5 rounded-full relative bg-theme-secondary/40">
              <span className={`absolute top-0.5 h-2 w-2 rounded-full transition-all ${heatmapOn ? 'bg-green-400 right-0.5' : 'bg-gray-400 left-0.5'}`}></span>
            </span>
          </button>
          {/* Sensitivity UI removed to avoid implying fixed thresholds */}
        </div>
      </div>
      <table className="w-full text-left border-separate border-spacing-0">
        <thead className="sticky top-0 z-20 bg-theme-secondary/95 border-b border-theme-tertiary shadow-sm">
          <tr key={'stock-comparison-header'} className="table-row">
            <th className="py-2.5 px-3.5 font-medium text-theme-secondary text-left text-xs sticky left-0 z-30 bg-theme-secondary/95">
              Metrics
            </th>
            {stocks.map(stock => (
              <th key={stock.symbol} className={`py-2.5 px-3.5 font-medium text-right text-xs ${stock.symbol === 'SPY' ? 'text-blue-400' : 'text-theme-primary'}`}>
                {stock.symbol}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableMetrics
            // Hide metrics where all selected stocks have null/undefined values
            .filter((metric: any) => stocks.some((s) => s?.metrics?.[metric.id]?.value !== null && s?.metrics?.[metric.id]?.value !== undefined))
            .map((metric: any, idx: number) => {
              const rowValues = stocks.map((s) => s?.metrics?.[metric.id]?.value ?? null);
              const rowStripe = idx % 2 === 0 ? 'bg-theme-primary/20' : 'bg-theme-primary/10';
              return (
                <tr key={metric.id} className={`table-row border-b border-theme-tertiary hover:bg-theme-tertiary/60 transition-colors ${rowStripe}`}>
                  <td className="py-2.5 px-3.5 text-left text-theme-secondary relative text-xs w-1/5 sticky left-0 z-10 bg-inherit">
                    {metric.label}
                  </td>
                  {stocks.map((stock, sidx) => {
                    const valObj: any = stock?.metrics?.[metric.id];
                    const val = valObj?.value;
                    if (val === undefined || val === null) {
                      return <td key={`${stock.symbol}-${metric.id}`} className="py-2.5 px-3.5 text-right text-theme-muted font-medium text-xs">N/A</td>;
                    }
                    const { className, style } = getCellStyle(metric.id, rowValues, sidx);
                    const tooltip = valObj?.tooltip || '';

                    return (
                      <td
                        key={`${stock.symbol}-${metric.id}`}
                        className={`py-2.5 px-3.5 text-right font-medium text-xs ${className} ${tooltip ? 'cursor-help' : ''}`}
                        style={style}
                      >
                        {tooltip ? (
                          <CustomTooltip content={tooltip}>
                            <span>{val} {valObj?.appendString}</span>
                          </CustomTooltip>
                        ) : (
                          <span>{val} {valObj?.appendString}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}
